import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Typography,
    Card,
    CardContent,
    Grid,
    Avatar,
    useTheme,
    alpha,
    IconButton
} from '@mui/material';
import {
    Business as EntityIcon,
    Person as IndividualIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import { BORROWER_TYPES, HIDE_INDIVIDUAL_BORROWER_OPTION } from '../../utils/constants';

const BorrowerTypeSelectModal = ({ open, handleClose, onSelectBorrowerType }) => {
    const theme = useTheme();

    const borrowerTypes = [
        {
            id: BORROWER_TYPES.INDIVIDUAL,
            title: 'Individual Borrower',
            description: 'Apply for a personal loan as an individual borrower',
            icon: <IndividualIcon fontSize="large" />,
            iconColor: theme.palette.primary.main,
            iconBgColor: alpha(theme.palette.primary.main, 0.15)
        },
        {
            id: BORROWER_TYPES.ENTITY,
            title: 'Business Entity',
            description: 'Apply for a business loan as a company, partnership, or proprietorship',
            icon: <EntityIcon fontSize="large" />,
            iconColor: theme.palette.secondary.main,
            iconBgColor: alpha(theme.palette.secondary.main, 0.15)
        }
    ].filter(
        (type) =>
            !HIDE_INDIVIDUAL_BORROWER_OPTION ||
            type.id !== BORROWER_TYPES.INDIVIDUAL
    );

    const handleSelectType = (typeId) => {
        onSelectBorrowerType(typeId);
        handleClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '24px',
                    boxShadow: '0 20px 60px rgba(0,0,0,0.15), 0 12px 30px rgba(0,0,0,0.08)',
                    overflow: 'hidden',
                    backgroundImage: theme.palette.mode === 'light'
                        ? 'linear-gradient(to bottom, #ffffff, #f8faff)'
                        : 'linear-gradient(to bottom, #1a1f2e, #131723)',
                    position: 'relative',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '5px',
                        background: `linear-gradient(90deg, 
                            ${theme.palette.primary.dark} 0%, 
                            ${theme.palette.primary.main} 50%,
                            ${theme.palette.primary.dark} 100%)`
                    }
                }
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 3,
                    background: alpha(theme.palette.primary.main, 0.04),
                    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`
                }}
            >
                <Typography
                    variant="h6"
                    component="div"
                    fontWeight="bold"
                    sx={{
                        color: theme.palette.text.primary,
                        fontFamily: "'Poppins', sans-serif",
                        letterSpacing: '0.02em',
                        fontSize: '1.125rem'
                    }}
                >
                    Select Borrower Type
                </Typography>
                <IconButton
                    edge="end"
                    onClick={handleClose}
                    aria-label="close"
                    sx={{
                        color: alpha(theme.palette.primary.main, 0.8),
                        bgcolor: alpha(theme.palette.primary.main, 0.08),
                        transition: 'all 0.2s',
                        '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.15),
                            transform: 'rotate(90deg)'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ p: 3 }}>
                <Typography 
                    variant="body1" 
                    sx={{ 
                        mb: 3, 
                        color: alpha(theme.palette.text.primary, 0.7),
                        fontFamily: "'Poppins', sans-serif" 
                    }}
                >
                    Please select the type of borrower for the loan application:
                </Typography>
                <Grid container spacing={2}>
                    {borrowerTypes.map((type) => (
                        <Grid item xs={12} md={6} key={type.id}>
                            <Card
                                onClick={() => handleSelectType(type.id)}
                                sx={{
                                    borderRadius: 4,
                                    cursor: 'pointer',
                                    height: '100%',
                                    transition: 'all 0.3s ease',
                                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                                    '&:hover': {
                                        transform: 'translateY(-4px)',
                                        boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
                                        borderColor: alpha(type.iconColor, 0.3),
                                        backgroundColor: alpha(type.iconColor, 0.02)
                                    }
                                }}
                            >
                                <CardContent>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 2 }}>
                                        <Avatar
                                            sx={{
                                                bgcolor: type.iconBgColor,
                                                color: type.iconColor,
                                                width: 80,
                                                height: 80,
                                                mb: 2
                                            }}
                                        >
                                            {type.icon}
                                        </Avatar>
                                        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1, textAlign: 'center' }}>
                                            {type.title}
                                        </Typography>
                                        <Typography 
                                            variant="body2" 
                                            color="textSecondary" 
                                            sx={{ 
                                                textAlign: 'center',
                                                fontFamily: "'Poppins', sans-serif" 
                                            }}
                                        >
                                            {type.description}
                                        </Typography>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </DialogContent>
            <DialogActions
                sx={{
                    px: 4,
                    py: 3,
                    background: alpha(theme.palette.primary.main, 0.03),
                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    justifyContent: 'center'
                }}
            >
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    sx={{
                        borderRadius: '10px',
                        textTransform: 'none',
                        px: 3,
                        py: 1.25,
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        borderColor: alpha(theme.palette.divider, 0.5),
                        color: theme.palette.text.secondary,
                        fontFamily: "'Poppins', sans-serif",
                        '&:hover': {
                            borderColor: theme.palette.primary.main,
                            backgroundColor: alpha(theme.palette.primary.main, 0.04)
                        }
                    }}
                >
                    Cancel
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BorrowerTypeSelectModal; 